import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import MobileMenu from './MobileMenu';

export default function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { scrollY } = useScroll();
    const location = useLocation();

    const isHome = location.pathname === '/';

    const headerBg = useTransform(
        scrollY,
        [0, 50],
        isHome ? ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 0.9)'] : ['rgba(255, 255, 255, 1)', 'rgba(255, 255, 255, 1)']
    );

    const headerBackdrop = useTransform(
        scrollY,
        [0, 50],
        isHome ? ['blur(0px)', 'blur(16px)'] : ['blur(0px)', 'blur(0px)']
    );

    const headerShadow = useTransform(
        scrollY,
        [0, 50],
        isHome ? ['none', '0 4px 6px -1px rgba(0, 0, 0, 0.1)'] : ['0 4px 6px -1px rgba(0, 0, 0, 0.1)', '0 4px 6px -1px rgba(0, 0, 0, 0.1)']
    );

    const logoScale = useTransform(scrollY, [0, 50], [1, 0.9]);
    const containerPadding = useTransform(scrollY, [0, 50], ['1.5rem', '1rem']);

    // Text colors for better visibility
    // Default text: White -> Primary-950 (Home), Always Primary-950 (Others)
    const textColor = useTransform(
        scrollY,
        [0, 50],
        isHome ? ['#ffffff', '#012E40'] : ['#012E40', '#012E40']
    );

    // Active link: Accent-500 (Teal) -> Primary-600 (Darker Teal) (Home)
    const activeColor = useTransform(
        scrollY,
        [0, 50],
        isHome ? ['#3CA6A6', '#026773'] : ['#026773', '#026773']
    );

    // Mobile menu button
    const mobileMenuColor = useTransform(
        scrollY,
        [0, 50],
        isHome ? ['#ffffff', '#012E40'] : ['#012E40', '#012E40']
    );

    const navLinks = [
        { name: 'Accueil', path: '/' },
        // Agenda removed as duplicate of Programme
        { name: 'À propos', path: '/a-propos' },
        { name: 'Photos', path: '/photos' },
        { name: 'Contact', path: '/contact' },
    ];

    return (
        <>
            <motion.header
                style={{
                    backgroundColor: headerBg,
                    backdropFilter: headerBackdrop,
                    boxShadow: headerShadow,
                }}
                className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
            >
                <motion.div
                    style={{ paddingBlock: containerPadding }}
                    className="container mx-auto px-6 flex items-center justify-between"
                >
                    <Link to="/" className="z-50">
                        <motion.div style={{ scale: logoScale }} className="flex items-center gap-2">
                            <img
                                src="/images/logo-uta-dark-2026.png"
                                alt="Logo UTA"
                                className="h-10 w-10 object-contain"
                            />
                            {/* Logo Text Color Animation */}
                            <motion.span
                                style={{ color: textColor }}
                                className="text-2xl font-serif font-bold"
                            >
                                UTA <span className="text-accent-500">Barvaux</span>
                            </motion.span>
                        </motion.div>
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => {
                            const isActive = location.pathname === link.path;
                            return (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className="font-medium transition-colors hover:text-primary-600 relative group"
                                >
                                    <motion.span style={{
                                        color: isActive ? activeColor : textColor,
                                    }}>
                                        {link.name}
                                    </motion.span>

                                    {isActive && (
                                        <motion.div
                                            layoutId="underline"
                                            className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent-500"
                                        />
                                    )}
                                </Link>
                            );
                        })}
                        <Link
                            to="/agenda"
                            className="px-5 py-2 bg-accent-500 text-primary-950 rounded-full font-bold hover:bg-accent-400 transition-colors shadow-lg shadow-accent-500/20"
                        >
                            Programme
                        </Link>
                    </nav>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="md:hidden z-50 p-2"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        <motion.div style={{ color: mobileMenuColor }}>
                            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </motion.div>
                    </button>
                </motion.div>
            </motion.header>

            <AnimatePresence>
                {isMobileMenuOpen && (
                    <MobileMenu
                        isOpen={isMobileMenuOpen}
                        onClose={() => setIsMobileMenuOpen(false)}
                        navLinks={navLinks}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
